/* eslint-disable no-nested-ternary */
import { useMemo, useState } from 'react';

import {
  ButtonV2 as Button,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChakraSwitch,
  ChakraText,
  CircleAdd,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';

import ErrorMessage from '~/components/common/ErrorMessage';
import {
  getActiveAndCompleteGoals,
  getActiveGoals,
  getNonActiveGoals,
} from '~/lib/util/goals.util';
import Loading from '~/pages/loading';
import { useGetPatientWeightGoalsQuery } from '~/store/services/clinicalData';

import EditWeightGoalItem from './EditWeightGoalItem';
import WeightGoalsList from './WeightGoalsList';
import {
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
} from '../../../constants';

const { captionBold, labelBold, label } = EquipUITheme.typography;
const { primary } = EquipUIFireflyV2Theme.colors;
type WeightGoalsProps = {
  patientId: string;
};

const WeightGoals = ({ patientId }: WeightGoalsProps) => {
  const {
    data: goalsData,
    isError: goalsError,
    isLoading: isGoalsDataLoading,
  } = useGetPatientWeightGoalsQuery({
    observationType:
      PatientVitalsTabsCollection[PatientVitalsTabs.WEIGHT].filterBy,
    patientExternalId: patientId,
  });

  const sortedGoalsData = useMemo<PatientWeightGoalType[]>(
    () => [
      ...getActiveGoals(goalsData ?? []).sort(
        (a, b) =>
          Number(new Date(b?.updatedAt)) - Number(new Date(a?.updatedAt)),
      ),
      ...getNonActiveGoals(goalsData ?? []).sort(
        (a, b) =>
          Number(new Date(b?.goalEndDate)) - Number(new Date(a?.goalEndDate)),
      ),
    ],
    [goalsData],
  );

  const [isAddingNewGoal, setIsAddingNewGoal] = useState<boolean>(false);
  const [isShowCompleteGoalsEnabled, setIsShowCompleteGoalsEnabled] =
    useState<boolean>(false);
  const filteredGoalsData = useMemo(
    () =>
      isShowCompleteGoalsEnabled
        ? getActiveAndCompleteGoals(sortedGoalsData || [])
        : getActiveGoals(sortedGoalsData || []),
    [sortedGoalsData, isShowCompleteGoalsEnabled],
  );
  const activeAndCompleteGoals = useMemo(
    () => getActiveAndCompleteGoals(sortedGoalsData || []),
    [sortedGoalsData],
  );

  const onNewGoalSaved = () => {
    setIsAddingNewGoal(false);
  };

  return (
    <ChakraBox marginBottom="28px">
      <ChakraFlex
        alignItems="center"
        justifyContent="center"
        padding="12px 0px 4px 0px"
      >
        <ChakraText
          {...captionBold}
          color="neutral.secondary"
          flexGrow={1}
          id="goals-title"
        >
          Goals
        </ChakraText>
        <ChakraHStack marginRight="16px">
          <ChakraFlex
            id="show-complete-goals-container"
            marginBottom="17px"
            marginTop="17px"
            overflowY="visible"
            position="relative"
          >
            <ChakraText
              fontSize="14px"
              id="show-complete-goals-label"
              lineHeight="17px"
              {...label}
            >
              Show completed
            </ChakraText>
            <ChakraSwitch
              colorScheme="teal"
              id="show-complete-goals-switch"
              isChecked={isShowCompleteGoalsEnabled}
              marginLeft="8px"
              onChange={() =>
                setIsShowCompleteGoalsEnabled(!isShowCompleteGoalsEnabled)
              }
              size="sm"
            />
          </ChakraFlex>
          <Button
            {...labelBold}
            id="add-goal"
            onClick={() => setIsAddingNewGoal(true)}
            rightIcon={
              <CircleAdd fill={primary[100]} height="12px" width="12px" />
            }
            size="xs"
            variant="link"
          >
            Add goal
          </Button>
        </ChakraHStack>
      </ChakraFlex>
      {isAddingNewGoal && (
        <EditWeightGoalItem
          goalsList={activeAndCompleteGoals}
          onCancel={() => setIsAddingNewGoal(false)}
          onSave={onNewGoalSaved}
          patientId={patientId}
        />
      )}
      {isGoalsDataLoading ? (
        <Loading />
      ) : goalsError ? (
        <ChakraBox paddingBottom="16px" textAlign="center">
          <ErrorMessage id="fetch-goals">{goalsError}</ErrorMessage>
        </ChakraBox>
      ) : (
        <WeightGoalsList
          filteredGoalsData={filteredGoalsData}
          goalsData={activeAndCompleteGoals}
          patientId={patientId}
        />
      )}
    </ChakraBox>
  );
};

export default WeightGoals;
